import { useState, useMemo, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import Button from '@material-ui/core/Button'
import { MdDragHandle, MdClose, MdAddCircle, MdPlayArrow } from 'react-icons/md'
import { uuid, array } from 'core/helpers/index'
import useInput from 'core/hooks/useInput'
import Dialog from '../../../../components/Modal/Dialog'
import { useAppContext } from '../../../../App'

const InputText = ({ initial, changed, placeholder, isWidgetForm }) => {
  const id = useMemo(() => uuid.generator(), [])
  const { bind } = useInput(initial, changed)
  const [charCount, setCharCount] = useState(initial?.length || 0)
  const [showError, setShowError] = useState(false)
  const { registerCharLimitError } = useAppContext()

  const handleChange = (e) => {
    const newValue = e.target.value
    setCharCount(newValue.length)
    const error = isWidgetForm && newValue.length > 140
    setShowError(error)
    registerCharLimitError(id, error)
    bind.onChange(e)
  }

  return (
    <div style={{ flexGrow: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <input
          {...bind}
          value={bind.value}
          onChange={handleChange}
          type="text"
          placeholder={placeholder}
          className="custom-field-values-editor-field"
          style={{
            flexGrow: 1,
            borderBottom: `1px solid ${
              showError ? '#EF3E39' : 'rgba(0,0,0,0.42)'
            }`,
            outline: 'none',
            padding: '4px 0',
            height: isWidgetForm ? '19px' : '30px'
          }}
        />
        {isWidgetForm && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '4px'
            }}
          >
            <span style={{ fontSize: '12px', color: '#9E9E9E' }}>
              {charCount}/140
            </span>
          </div>
        )}
      </div>

      {showError && (
        <div>
          <span
            style={{ color: '#EF3E39', fontSize: '12px', marginTop: '4px' }}
          >
            O campo deve ter no máximo 140 caracteres.
          </span>
        </div>
      )}
    </div>
  )
}

const CustomFieldValuesEditor = ({
  FormFieldValues,
  onChange,
  t,
  isWidgetForm
}) => {
  const initialValues =
    FormFieldValues && FormFieldValues.length > 0
      ? FormFieldValues.map((val, idx) => ({
          ...val
        }))
      : [
          {
            Id: 1,
            Value: '',
            Key: uuid.generator()
          },
          {
            Id: 2,
            Value: '',
            Key: uuid.generator()
          }
        ]

  const [values, setValues] = useState(initialValues)
  const [isRemovingValue, setIsRemovingValue] = useState(-1)
  const { showMinDimensionWarning, setShowMinDimensionWarning } =
    useAppContext()

  const changeValue = (val, idx) => {
    const newValues = [...values]
    newValues[idx].Value = val
    setValues(newValues)
    onChange(newValues)
  }

  const removeItem = (idx) => {
    const newValues = [...values]
    newValues.splice(idx, 1)
    setValues(newValues)
    onChange(newValues)
    setIsRemovingValue(-1)
  }

  const addItem = () => {
    const newValue = {
      Id: null,
      Value: '',
      Key: uuid.generator()
    }

    const updatedValues = [...values, newValue]
    setValues(updatedValues)
    onChange(updatedValues)
  }

  const onDragEnd = (result) => {
    if (!result.destination) return
    const newValues = array.reorder(
      values,
      result.source.index,
      result.destination.index
    )
    setValues(newValues)
    onChange(newValues)
  }

  useEffect(() => {
    if (isWidgetForm) {
      const filledCount = values.filter(
        (v) => v.Value && v.Value.trim() !== ''
      ).length
      setShowMinDimensionWarning(filledCount < 2)
    }
  }, [values, isWidgetForm, setShowMinDimensionWarning])

  return (
    <div className="custom-field-values-editor">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`droppable-content custom-field-values-editor-list ${
                snapshot.isDraggingOver ? 'droppable-content-dragging' : ''
              }`}
            >
              {values.map((field, fidx) => (
                <Draggable key={field.Key} draggableId={field.Key} index={fidx}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={`custom-field-values-editor-item droppable-item ${
                        snapshot.isDragging ? 'droppable-item-dragging' : ''
                      }`}
                    >
                      <MdDragHandle className="custom-field-values-editor-drag" />
                      <MdPlayArrow className="custom-field-values-editor-arrow" />
                      <InputText
                        initial={field.Value || ''}
                        changed={(val) => changeValue(val, fidx)}
                        placeholder={`${t('formEditor.field.values.mock')} ${
                          fidx + 1
                        }`}
                        isWidgetForm={isWidgetForm}
                      />

                      {values.length > 2 && (
                        <Button
                          type="button"
                          onClick={() => setIsRemovingValue(fidx)}
                          className="btn-page-action btn-page-action-small cancel btn-page-action-cancel"
                        >
                          <MdClose />
                        </Button>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Button
        color="primary"
        type="button"
        onClick={addItem}
        disabled={isWidgetForm && values.length >= 3}
      >
        <MdAddCircle className="custom-field-values-editor-add-icon" />
        {t('formEditor.field.values.addValue')}
      </Button>

      {isWidgetForm && showMinDimensionWarning && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <span
            style={{
              color: '#EF3E39',
              marginTop: 8,
              textAlign: 'center',
              fontSize: '12px'
            }}
          >
            Por favor, insira no mínimo 2 respostas com conteúdo.
          </span>
        </div>
      )}

      <Dialog
        show={isRemovingValue > -1}
        onClose={() => setIsRemovingValue(-1)}
        onCancel={() => setIsRemovingValue(-1)}
        onAccept={() => removeItem(isRemovingValue)}
      >
        <h3 className="modal-title">
          {t('formEditor.field.values.deletingTitle')}
        </h3>
        <p className="modal-text">
          {t('formEditor.field.values.deletingQuestion')}
          <br />
          <strong>{t('formEditor.field.values.deletingWarning')}</strong>
        </p>
      </Dialog>
    </div>
  )
}

export default withTranslation()(CustomFieldValuesEditor)
